import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Building2, Home as HomeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Fix Leaflet default marker icons for bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom orange/purple SVG icons (HTML divIcon for crispness)
const makeDivIcon = (color) =>
  L.divIcon({
    className: 'gc-pin',
    html: `<div style="
      width:38px;height:38px;display:flex;align-items:center;justify-content:center;
      background:${color};border:3px solid white;border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);box-shadow:0 6px 16px rgba(0,0,0,.35);
    "><div style="transform:rotate(45deg);color:white;font-weight:700;font-size:14px;">${
      color === '#FF8C00' ? 'M' : 'I'
    }</div></div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 36],
    popupAnchor: [0, -32],
  });

const houseIcon = makeDivIcon('#FF8C00'); // M = maison
const buildingIcon = makeDivIcon('#702963'); // I = immeuble

const FitBounds = ({ projects }) => {
  const map = useMap();
  useEffect(() => {
    if (!projects.length) return;
    const bounds = L.latLngBounds(projects.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, projects]);
  return null;
};

const KinshasaMapSection = () => {
  const { language } = useLanguage();

  // Sample portfolio — admin can later edit this list (or move to backend).
  const projects = useMemo(
    () => [
      {
        id: 'p1',
        name: 'Villa Gombe Riverside',
        type: 'house',
        commune: 'Gombe',
        address: language === 'fr' ? 'Avenue du Fleuve, Gombe' : 'Fleuve Ave., Gombe',
        year: 2024,
        lat: -4.3035,
        lng: 15.3107,
        photo:
          'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=70',
      },
      {
        id: 'p2',
        name: 'Résidence Limete Park',
        type: 'house',
        commune: 'Limete',
        address: language === 'fr' ? '15ᵉ rue, Limete' : '15th street, Limete',
        year: 2023,
        lat: -4.348,
        lng: 15.3322,
        photo:
          'https://images.unsplash.com/photo-1613553497126-a44624272024?auto=format&fit=crop&w=800&q=70',
      },
      {
        id: 'p3',
        name: 'Immeuble Lemba Business Center',
        type: 'building',
        commune: 'Lemba',
        address: language === 'fr' ? 'Boulevard Lumumba, Lemba' : 'Lumumba Bld., Lemba',
        year: 2024,
        lat: -4.376,
        lng: 15.302,
        photo:
          'https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=800&q=70',
      },
      {
        id: 'p4',
        name: 'Tour Kintambo Heights',
        type: 'building',
        commune: 'Kintambo',
        address: language === 'fr' ? 'Avenue Kasa-Vubu, Kintambo' : 'Kasa-Vubu Ave., Kintambo',
        year: 2022,
        lat: -4.319,
        lng: 15.273,
        photo:
          'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=800&q=70',
      },
      {
        id: 'p5',
        name: 'Villa Mont-Ngafula Hills',
        type: 'house',
        commune: 'Mont-Ngafula',
        address: language === 'fr' ? 'Route de Matadi, Mont-Ngafula' : 'Matadi road, Mont-Ngafula',
        year: 2025,
        lat: -4.4612,
        lng: 15.275,
        photo:
          'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=70',
      },
      {
        id: 'p6',
        name: 'Immeuble Ngaliema Office',
        type: 'building',
        commune: 'Ngaliema',
        address: language === 'fr' ? 'Avenue de la Justice, Ngaliema' : 'Justice Ave., Ngaliema',
        year: 2023,
        lat: -4.358,
        lng: 15.232,
        photo:
          'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=70',
      },
    ],
    [language]
  );

  const _counts = useMemo(
    () => ({
      all: projects.length,
      house: projects.filter((p) => p.type === 'house').length,
      building: projects.filter((p) => p.type === 'building').length,
    }),
    [projects]
  );

  const filtered = projects;

  return (
    <section className="py-20 md:py-24 bg-white" data-testid="kinshasa-map-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <span className="inline-block text-orange-500 font-semibold tracking-widest text-sm mb-3">
            {language === 'fr' ? 'NOS RÉALISATIONS' : 'OUR PROJECTS'}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-3" style={{ color: '#702963' }} data-testid="map-title">
            {language === 'fr' ? 'Quelques de nos réalisations à Kinshasa' : 'Some of our projects in Kinshasa'}
          </h2>
          <div className="mx-auto h-[3px] w-20 bg-orange-500 rounded-full" />
          <p className="mt-5 text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Découvrez sur la carte les maisons et immeubles que nous avons réalisés dans la capitale.'
              : 'Discover on the map the houses and buildings we have completed in the capital.'}
          </p>
        </div>

        {/* Map */}
        <div
          className="relative rounded-sm overflow-hidden shadow-2xl border border-gray-200"
          style={{ height: '520px', isolation: 'isolate', zIndex: 0 }}
          data-testid="leaflet-map-wrapper"
        >
          <MapContainer
            center={[-4.34, 15.31]}
            zoom={11}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds projects={filtered} />
            {filtered.map((p) => (
              <Marker
                key={p.id}
                position={[p.lat, p.lng]}
                icon={p.type === 'building' ? buildingIcon : houseIcon}
              >
                <Popup minWidth={240} maxWidth={280}>
                  <div className="font-sans" data-testid={`popup-${p.id}`}>
                    <img
                      src={p.photo}
                      alt={p.name}
                      className="w-full h-32 object-cover rounded-sm mb-2"
                    />
                    <div className="flex items-center gap-2 text-xs text-orange-600 font-bold uppercase tracking-wider mb-1">
                      {p.type === 'building' ? (
                        <Building2 className="w-3.5 h-3.5" />
                      ) : (
                        <HomeIcon className="w-3.5 h-3.5" />
                      )}
                      <span>
                        {p.type === 'building'
                          ? (language === 'fr' ? 'Immeuble' : 'Building')
                          : (language === 'fr' ? 'Maison' : 'House')}
                      </span>
                      <span className="text-gray-400">• {p.year}</span>
                    </div>
                    <h4 className="text-base font-bold text-gray-900 mb-1 leading-tight">
                      {p.name}
                    </h4>
                    <p className="text-xs text-gray-600 flex items-start gap-1.5">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#702963]" />
                      <span>
                        {p.address}
                        <br />
                        <span className="text-gray-500">{p.commune}, Kinshasa</span>
                      </span>
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Legend overlay (top-right) */}
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm shadow-lg p-3 text-xs space-y-1.5 rounded-sm z-[400]">
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#FF8C00' }} />
              <span>{language === 'fr' ? 'Maisons' : 'Houses'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#702963' }} />
              <span>{language === 'fr' ? 'Immeubles' : 'Buildings'}</span>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-sm text-gray-500 mt-5 italic">
          {language === 'fr'
            ? `${projects.length} projets référencés à Kinshasa — cliquez sur un repère pour voir le détail.`
            : `${projects.length} projects on the map — click a pin to view details.`}
        </p>
      </div>
    </section>
  );
};

export default KinshasaMapSection;
